import { CartonGrouping, Quantity } from '@app/generated/graphql';
import { TitleCasePipe } from '@angular/common';
import { EmptyPipe } from '../pipes/empty.pipe';
import { ReplacePipe } from '../pipes/replace.pipe';
import { CurrencyPipe } from '../pipes/currency.pipe';
import { QuantityHelper } from '../helpers/quantity.helper';
import { MajorUnitPipe } from '@app/core/pipes/major-unit.pipe';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import advancedFormat from 'dayjs/plugin/advancedFormat';

dayjs.extend(isoWeek);
dayjs.extend(advancedFormat);

export class CartonGroupingViewModel {
  cartonGrouping: CartonGrouping;
  currencyPipe = new CurrencyPipe();
  empty = new EmptyPipe();
  titleCase = new TitleCasePipe();
  replace = new ReplacePipe();
  majorUnitPipe = new MajorUnitPipe();

  payment_type: string;

  constructor(cartonGrouping: CartonGrouping, payment_type?: string) {
    this.cartonGrouping = cartonGrouping;
    this.payment_type = payment_type;
  }

  get paymentType(): string {
    return this.payment_type;
  }

  get lineItemId(): number {
    return this.cartonGrouping.lineItemId;
  }

  get buyerName(): string {
    return this.empty.transform(this.cartonGrouping.buyer?.name);
  }

  get coldStore(): string {
    return this.empty.transform(this.cartonGrouping.coldStore);
  }

  get sellerName(): string {
    return this.empty.transform(this.cartonGrouping.seller?.name);
  }

  get containerNumber(): string {
    return this.empty.transform(this.cartonGrouping.containerNumber);
  }

  get exporterCode(): string {
    return this.empty.transform(this.cartonGrouping.exporterCode);
  }

  get farmCode(): string {
    return this.empty.transform(this.cartonGrouping.farmCode);
  }

  get grade(): string {
    return this.empty.transform(this.cartonGrouping.grade);
  }

  get orchard(): string {
    return this.empty.transform(this.cartonGrouping.orchard);
  }

  get pack(): string {
    return this.empty.transform(this.cartonGrouping.pack);
  }

  get packhouseCode(): string {
    return this.empty.transform(this.cartonGrouping.packhouseCode);
  }

  get packingWeek(): string {
    return this.empty.transform(this.cartonGrouping.packingWeek);
  }

  get palletNumber(): string {
    return this.empty.transform(this.cartonGrouping.palletNumber);
  }

  get productCharacteristic(): string {
    return this.empty.transform(this.cartonGrouping.productCharacteristic);
  }

  get sequenceNumber(): number {
    return this.empty.transform(this.cartonGrouping.sequenceNumber);
  }

  get targetCountry(): string {
    return this.empty.transform(this.cartonGrouping.targetCountry);
  }

  get targetMarket(): string {
    return this.empty.transform(this.cartonGrouping.targetMarket);
  }

  get targetRegion(): string {
    return this.cartonGrouping.targetRegion;
  }

  get cartons(): number {
    return this.cartonGrouping.cartons;
  }

  get pallets(): number {
    return QuantityHelper.convertQuantity(
      this.cartonGrouping.cartons,
      this.cartonGrouping.palletStack,
      Quantity.Carton,
      Quantity.Pallet,
    );
  }

  get containers(): number {
    return QuantityHelper.convertQuantity(
      this.cartonGrouping.cartons,
      this.cartonGrouping.palletStack,
      Quantity.Carton,
      Quantity.Container,
    );
  }

  get state(): string {
    return this.titleCase.transform(
      this.replace.transform(this.cartonGrouping.state, '_', ' '),
    );
  }

  get totalAdvancePrice(): number {
    return this.cartonGrouping.advancePrice
      ? this.majorUnitPipe.transform(this.cartonGrouping.advancePrice)
      : null;
  }

  get dueDate(): string {
    if (this.cartonGrouping.advanceDueDate) {
      return dayjs(this.cartonGrouping.advanceDueDate).format('GGGG[-W]WW');
    } else if (this.cartonGrouping.finalDueDate) {
      return dayjs(this.cartonGrouping.finalDueDate).format('GGGG[-W]WW');
    } else return '-';
  }

  get totalFinalPrice(): number {
    return this.cartonGrouping.finalPrice
      ? this.majorUnitPipe.transform(this.cartonGrouping.finalPrice)
      : null;
  }

  get totalPrice(): number {
    return this.totalAdvancePrice + this.totalFinalPrice;
  }

  get variety(): string {
    return this.cartonGrouping.variety.name;
  }

  get commodity(): string {
    return this.cartonGrouping.variety.commodity.name;
  }

  get palletStack(): number {
    return this.cartonGrouping.palletStack;
  }

  get referenceNumber(): string {
    return this.cartonGrouping.lineItem?.referenceNumber;
  }

  get sizeCount(): string {
    return this.cartonGrouping.sizeCount;
  }

  get consignmentNumber(): string {
    return this.cartonGrouping.consignmentNumber;
  }

  get batchNumber(): string {
    return this.cartonGrouping.batchNumber;
  }

  get inventoryCode(): string {
    return this.cartonGrouping.inventoryCode;
  }

  get orderId(): number {
    return this.cartonGrouping.lineItem?.orderId;
  }

  get incoterm(): string {
    return this.cartonGrouping.lineItem?.incoterm;
  }

  get mark(): string {
    return this.cartonGrouping.lineItem?.mark;
  }
}
